/**
 * MessageBubble - Presentation component for displaying individual chat messages
 * 
 * Follows ADR principles:
 * - ADR-003: High Cohesion - Single responsibility: display a message
 * - ADR-004: Separation of Concerns - Pure presentation component
 * - ADR-005: Information Hiding - Minimal props interface
 */

import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { messageBubblePropsEqual } from '../utils/messageBubblePropsEqual';
import { AlertTriangle, FileText, ChevronDown, ChevronUp, Copy, Files, Check } from 'lucide-react';
import type { MessageInterface } from '../hooks/useMessageProcessing';
import { MessageContent } from './MessageContent';
import { formatMessageContent } from '../utils/messageFormat';
import { extractStreamingMessage, isMessageStreaming } from '../types/parlant';
import type { ButtonVariant } from '../types/theme';
import { logDebug } from '../utils/logger';

/**
 * Regex pattern for detecting markdown table separator lines
 * 
 * Note: Uses RegExp constructor with string concatenation to prevent Tailwind CSS
 * from incorrectly interpreting `[-:\s|]` as an arbitrary value class.
 */
const TABLE_SEPARATOR_PATTERN = new RegExp('^\\|[-:\\s' + '|]+\\|$');

/**
 * Props for MessageBubble component
 */
interface MessageBubbleProps {
  /** Message data to display */
  message: MessageInterface;
  /** Display name for the message source */
  displayName: string;
  /** Formatted timestamp string */
  timestamp: string;
  /** Whether this is a status/typing message */
  isStatusMessage?: boolean;
  /** Optional callback when a button in the message is clicked */
  onButtonClick?: (url: string, label: string, variant?: ButtonVariant) => void;
  /** Whether this is the last agent message (shows copy button) */
  isLastAgentMessage?: boolean;
  /** Text to copy when copy button is clicked (all messages since last user message) */
  copyableText?: string;
  /** Tooltip for the copy button */
  copyTooltip?: string;
  /** Whether this message continues from the previous message (same sender, same correlation) */
  isContinual?: boolean;
  /** Callback to show logs for this message */
  onShowLogs?: () => void;
  /** Translation function */
  t?: (key: string, params?: Record<string, string>) => string;
  /** Whether debug mode is enabled (shows draft, logs, etc.) */
  debugMode?: boolean;
  /** Layout mode - in popup mode, agent messages can use full width */
  layoutMode?: 'popup' | 'fullscreen';
}

/**
 * Different animation variants for the typing indicator
 * Cycles through these to give users something interesting to watch
 */
type TypingVariant = 'bounce' | 'pulse' | 'wave' | 'fade' | 'scale';

const TYPING_VARIANTS: TypingVariant[] = ['bounce', 'pulse', 'wave', 'fade', 'scale'];

/**
 * TypingIndicator - Animated dots that cycle through different styles
 * Changes animation every ~800ms to make wait time feel shorter
 */
const TypingIndicator: React.FC = () => {
  const [variantIndex, setVariantIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setVariantIndex((i) => (i + 1) % TYPING_VARIANTS.length);
    }, 800);

    return () => clearInterval(timer);
  }, []);

  const variant = TYPING_VARIANTS[variantIndex];

  // Get animation classes based on current variant
  const getAnimationClasses = (): string => {
    switch (variant) {
      case 'bounce':
        return 'animate-bounce';
      case 'pulse':
        return 'animate-pulse';
      case 'wave':
        return 'animate-bounce';
      case 'fade':
        return 'animate-pulse';
      case 'scale':
        // Use pulse instead of ping to avoid overflow/layout shift
        return 'animate-pulse';
      default:
        return 'animate-bounce';
    }
  };

  // Get style variations for each dot
  const getDotStyle = (index: number): React.CSSProperties => {
    const baseDelay = index * 150;
    
    switch (variant) {
      case 'bounce':
        return { animationDelay: `${baseDelay}ms` };
      case 'pulse':
        return { animationDelay: `${baseDelay}ms`, animationDuration: '1s' };
      case 'wave':
        return { animationDelay: `${baseDelay}ms`, animationDuration: '0.6s' };
      case 'fade':
        return { animationDelay: `${index * 200}ms`, animationDuration: '1.5s' };
      case 'scale':
        return { animationDelay: `${baseDelay}ms`, animationDuration: '1.5s' };
      default:
        return { animationDelay: `${baseDelay}ms` };
    }
  };

  return (
    // Fixed width container to prevent layout shift when animations change
    <div className="chatbot-typing-indicator flex items-center justify-center w-8 h-4">
      <div className="flex space-x-1">
        <div 
          className={`chatbot-typing-dot w-2 h-2 bg-current opacity-60 rounded-full ${getAnimationClasses()}`} 
          style={getDotStyle(0)} 
        />
        <div 
          className={`chatbot-typing-dot w-2 h-2 bg-current opacity-60 rounded-full ${getAnimationClasses()}`} 
          style={getDotStyle(1)} 
        />
        <div 
          className={`chatbot-typing-dot w-2 h-2 bg-current opacity-60 rounded-full ${getAnimationClasses()}`} 
          style={getDotStyle(2)} 
        />
      </div>
    </div>
  );
};

/**
 * PendingIndicator - Subtle animated dots for pending message status
 * Uses a gentle opacity pulse that doesn't cause layout shifts
 * Has a fixed width to prevent layout shifts when appearing/disappearing
 */
interface PendingIndicatorProps {
  /** Whether the indicator is visible (controls opacity, not DOM presence) */
  visible?: boolean;
}

const PendingIndicator: React.FC<PendingIndicatorProps> = ({ visible = true }) => (
  <div 
    className={`chatbot-pending-indicator flex items-center gap-1 w-5 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
    aria-hidden={!visible}
  >
    <div className="chatbot-pending-dot w-1 h-1 bg-current rounded-full animate-pending-pulse" />
    <div className="chatbot-pending-dot w-1 h-1 bg-current rounded-full animate-pending-pulse" style={{ animationDelay: '0.2s' }} />
    <div className="chatbot-pending-dot w-1 h-1 bg-current rounded-full animate-pending-pulse" style={{ animationDelay: '0.4s' }} />
  </div>
);

/**
 * MessageBubble - Pure presentation component for rendering chat messages
 * Both customer and agent messages share the same layout structure
 * 
 * @example
 * ```tsx
 * <MessageBubble
 *   message={message}
 *   displayName="Agent"
 *   timestamp="10:30 AM"
 * />
 * ```
 */
const MessageBubbleComponent: React.FC<MessageBubbleProps> = ({
  message,
  displayName,
  timestamp,
  isStatusMessage = false,
  onButtonClick,
  isLastAgentMessage = false,
  copyableText,
  copyTooltip,
  isContinual = false,
  onShowLogs,
  t = (key: string) => key,
  debugMode = false,
  layoutMode = 'fullscreen',
}) => {
  const [showDraft, setShowDraft] = useState(false);
  const [copiedSingle, setCopiedSingle] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);
  const bubbleRef = useRef<HTMLDivElement>(null);
  
  const isCustomer = message.source === 'customer' || message.source === 'customer_ui';
  const isHumanAgent = message.source === 'human_agent';
  const isPending = message.serverStatus === 'pending';
  const isError = message.serverStatus === 'error' || message.status === 'error';
  const isFlagged = message.flagged === true;
  const hasDraft = !isCustomer && !!message.draft;

  // Streaming state — true while SSE chunks are still arriving for this bubble
  const streaming =
    !isCustomer && isMessageStreaming(message.data as Record<string, unknown>);
  const isInterrupted =
    !isCustomer && !!(message.data as Record<string, unknown>)?._interrupted;

  // Full text assembled from chunks, falling back to the static `message`
  // field for non-streaming (history / single-shot) messages.
  const assembledText =
    extractStreamingMessage(message.data as Record<string, unknown>) ||
    (message.data as { message?: string | Record<string, unknown> })?.message;

  const messageText = formatMessageContent(
    typeof assembledText === 'string' ? assembledText : assembledText
  );
  const hasVisibleContent = messageText.trim().length > 0;
  const displayStatusText = message.displayStatusText;
  const hasInlineStatus = Boolean(displayStatusText?.trim());

  // Hide agent shells until there is something to show: visible text, inline
  // status text, or an active stream. Never mount a cursor-only empty bubble.
  const shouldSuppressEmptyAgentBubble =
    !isCustomer &&
    !isStatusMessage &&
    !hasVisibleContent &&
    !hasInlineStatus &&
    !streaming &&
    !isError &&
    !isInterrupted &&
    !(hasDraft && debugMode);

  // Plain-text mode while streaming avoids broken partial markdown tokens.
  const useLightweightContentRendering = streaming && !isInterrupted;

  // Inline cursor at the end of visible text while SSE chunks are still open.
  const showCursor =
    !isCustomer &&
    !isStatusMessage &&
    streaming &&
    hasVisibleContent &&
    !isInterrupted;

  // Helper function to get bubble position and log (only when debugMode is enabled)
  const logWithPosition = useCallback((location: string, message: string, data: Record<string, unknown>) => {
    // Only log when debug mode is enabled
    if (!debugMode) return;
    
    const position = bubbleRef.current?.getBoundingClientRect();
    const positionData = position ? {
      x: Math.round(position.left),
      y: Math.round(position.top),
      width: Math.round(position.width),
      height: Math.round(position.height)
    } : { x: null, y: null, width: null, height: null };
    
    logDebug(`[${location}] ${message}`, {
      ...data,
      position: positionData,
      timestamp: Date.now(),
      sessionId: 'debug-session',
      hypothesisId: 'RENDER'
    });
  }, [debugMode]);

  // #region agent log - Track renders
  const msgPreview = messageText?.substring(0, 20) || '[empty]';
  const bubbleType = isStatusMessage ? 'STATUS' : (isCustomer ? 'CUSTOMER' : 'AGENT');
  
  useEffect(() => {
    // Log on mount with position
    logWithPosition(
      'MessageBubble.tsx:mount',
      `🟢 MOUNT ${bubbleType}`,
      {
        id: message.id,
        offset: message.offset,
        status: message.status,
        text: msgPreview,
        isStatusMessage
      }
    );
    
    // Log on unmount
    return () => {
      logWithPosition(
        'MessageBubble.tsx:unmount',
        `🔴 UNMOUNT ${bubbleType}`,
        {
          id: message.id,
          offset: message.offset,
          status: message.status,
          text: msgPreview,
          isStatusMessage
        }
      );
    };
  }, [message.id, bubbleType, msgPreview, message.offset, message.status, isStatusMessage, logWithPosition]);
  
  // Log every render with position
  useEffect(() => {
    logWithPosition(
      'MessageBubble.tsx:render',
      `🔄 RENDER ${bubbleType}`,
      {
        id: message.id,
        offset: message.offset,
        status: message.status,
        text: msgPreview,
        isStatusMessage
      }
    );
  });
  // #endregion

  /**
   * Converts markdown to clean plain text for copying
   * - Removes bold/italic markers (**, *, __, _)
   * - Converts tables to tab-separated text
   * - Removes link syntax [text](url) → text
   * - Removes button syntax [btn:text](url) → text
   * - Preserves line breaks
   */
  const markdownToPlainText = useCallback((md: string): string => {
    if (!md) return md;
    
    let text = md;
    
    // Convert markdown tables to plain text with tabs
    // Match table rows: | col1 | col2 | col3 |
    const tableRowRegex = /^\|(.+)\|$/gm;
    const hasTable = tableRowRegex.test(text);
    
    if (hasTable) {
      // Split into lines and process table
      const lines = text.split('\n');
      const processedLines: string[] = [];
      
      for (const line of lines) {
        // Skip separator lines (|---|---|)
        if (TABLE_SEPARATOR_PATTERN.test(line)) {
          continue;
        }
        // Convert table rows to tab-separated
        if (/^\|.+\|$/.test(line)) {
          const cells = line
            .slice(1, -1) // Remove leading/trailing |
            .split('|')
            .map(cell => cell.trim());
          processedLines.push(cells.join('\t'));
        } else {
          processedLines.push(line);
        }
      }
      text = processedLines.join('\n');
    }
    
    // Remove button syntax [btn:label](url) or [btn:label:variant](url) → label
    text = text.replace(/\[btn:([^\]:]+)(?::[^\]]+)?\]\([^)]+\)/g, '$1');
    
    // Remove link syntax [text](url) → text
    text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    
    // Remove bold markers (** or __)
    text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
    text = text.replace(/__([^_]+)__/g, '$1');
    
    // Remove italic markers (* or _) - be careful not to affect underscores in words
    text = text.replace(/\*([^*]+)\*/g, '$1');
    text = text.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '$1');
    
    // Remove strikethrough markers (~~)
    text = text.replace(/~~([^~]+)~~/g, '$1');
    
    // Remove heading markers (# ## ### etc.)
    text = text.replace(/^#{1,6}\s+/gm, '');
    
    // Convert blockquotes (> text) to plain text
    text = text.replace(/^>\s?/gm, '');
    
    // Convert horizontal rules to dashes
    text = text.replace(/^([-*_]){3,}\s*$/gm, '---');
    
    // Remove code block markers
    text = text.replace(/```[\s\S]*?```/g, (match) => {
      // Keep the content, remove the markers
      return match.replace(/^```\w*\n?/, '').replace(/\n?```$/, '');
    });
    
    // Remove inline code markers
    text = text.replace(/`([^`]+)`/g, '$1');
    
    // Clean up escaped characters
    text = text.replace(/\\([*_`[\]()#])/g, '$1');
    
    // Normalize multiple newlines to max 2
    text = text.replace(/\n{3,}/g, '\n\n');
    
    return text.trim();
  }, []);

  // Copy to clipboard helper
  const copyToClipboard = useCallback(async (text: string): Promise<boolean> => {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // Fall through to fallback
      }
    }
    // Fallback for older browsers
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
      document.body.appendChild(textArea);
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch {
      return false;
    }
  }, []);

  // Handle copying single message (converted to plain text)
  const handleCopySingle = useCallback(async () => {
    const plainText = markdownToPlainText(messageText);
    const success = await copyToClipboard(plainText);
    if (success) {
      setCopiedSingle(true);
      setTimeout(() => setCopiedSingle(false), 2000);
    }
  }, [copyToClipboard, messageText, markdownToPlainText]);

  // Handle copying all messages (converted to plain text)
  const handleCopyAll = useCallback(async () => {
    if (!copyableText) return;
    const plainText = markdownToPlainText(copyableText);
    const success = await copyToClipboard(plainText);
    if (success) {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }
  }, [copyToClipboard, copyableText, markdownToPlainText]);

  // Determine bubble styling based on source and status
  const getBubbleStyles = (): string => {
    // Error state takes precedence
    if (isError && isCustomer) {
      return 'chatbot-message-error bg-destructive/10 text-destructive border-[0.5px] border-destructive/50';
    }
    if (isCustomer) {
      return isPending
        ? 'chatbot-message-customer chatbot-message-pending bg-muted text-muted-foreground border-[0.5px] border-primary/50'
        : 'chatbot-message-customer bg-primary text-primary-foreground';
    }
    if (isHumanAgent) {
      return 'chatbot-message-human-agent bg-accent text-accent-foreground';
    }
    return 'chatbot-message-agent bg-card border-[0.5px] border-border/50 text-card-foreground';
  };

  // Determine text styling for header elements
  const getHeaderTextStyles = (): { name: string; time: string } => {
    if (isError && isCustomer) {
      return {
        name: 'text-destructive',
        time: 'text-destructive/70'
      };
    }
    if (isCustomer) {
      return {
        name: isPending ? 'text-muted-foreground' : 'text-primary-foreground',
        time: isPending ? 'text-muted-foreground' : 'text-primary-foreground/80'
      };
    }
    if (isHumanAgent) {
      return {
        name: 'text-accent-foreground',
        time: 'text-accent-foreground/70'
      };
    }
    return {
      name: 'text-muted-foreground',
      time: 'text-muted-foreground'
    };
  };

  const headerStyles = getHeaderTextStyles();

  // Determine border radius based on position and continuity
  const getBorderRadius = (): string => {
    if (isContinual) {
      // Continual message: flat top, normal bottom
      return isCustomer ? 'rounded-lg rounded-t-sm rounded-br-sm' : 'rounded-lg rounded-t-sm rounded-bl-sm';
    }
    // First message in group: normal top, small corner on sender side
    return isCustomer ? 'rounded-lg rounded-br-sm' : 'rounded-lg rounded-bl-sm';
  };

  // In popup mode, agent messages can use full width to reduce vertical scrolling
  // This is especially useful for tables and wide content
  const getMaxWidthClass = (): string => {
    if (isCustomer) {
      // Customer messages always have limited width
      return 'max-w-[90%] sm:max-w-[80%]';
    }
    if (layoutMode === 'popup') {
      // Agent messages in popup mode can use full width
      return 'max-w-full';
    }
    // Agent messages in fullscreen mode have normal width
    return 'max-w-[90%] sm:max-w-[80%]';
  };

  if (shouldSuppressEmptyAgentBubble) {
    return null;
  }

  return (
    <div className={`chatbot-message flex ${isCustomer ? 'justify-end' : 'justify-start'}`} style={{ contain: 'layout style' }}>
      <div
        ref={bubbleRef}
        data-layout-message-id={message.id}
        data-layout-message-offset={String(message.offset)}
        className={`group relative min-w-[200px] ${getMaxWidthClass()} p-3 sm:p-4 shadow-lg ${getBubbleStyles()} ${getBorderRadius()}${isStatusMessage ? ' chatbot-message-status' : ''}`}
      >
        {/* Message Header — hidden on continual messages to create a grouped
            thread-like look (pairs with the flattened corner in getBorderRadius). */}
        {!isContinual && (
          <div className="chatbot-message-header flex items-start justify-between mb-1.5 sm:mb-2 w-full">
            <div className="flex items-center gap-2 truncate min-w-0 flex-shrink">
              <span className={`chatbot-message-sender text-xs font-medium truncate ${headerStyles.name}`}>
                {displayName}
              </span>
              {isFlagged && (
                <span
                  className="chatbot-flagged-indicator text-amber-500"
                  title={message.flaggedReason || t('message.flagged')}
                >
                  <AlertTriangle size={12} />
                </span>
              )}
            </div>
            <div className="flex items-center flex-shrink-0 gap-1">
              {isError && isCustomer && (
                <span className="text-destructive" title={message.error || t('message.error')}>
                  <AlertTriangle size={12} />
                </span>
              )}
              {isCustomer && <PendingIndicator visible={isPending && !isError} />}
              <span className={`chatbot-message-timestamp text-xs ${headerStyles.time}`}>
                {timestamp}
              </span>
            </div>
          </div>
        )}

        {/* Error message for failed sends */}
        {isError && message.error && (
          <div className="chatbot-message-error-text text-xs text-destructive mb-2 flex items-center gap-1">
            <AlertTriangle size={12} />
            <span>{message.error}</span>
          </div>
        )}

        {/* Message Content — animate-content-reveal plays once on mount for a
            gentle fade-up entrance. The animation is forwards + mount-scoped,
            so it doesn't re-trigger on re-render (e.g. streaming chunks). */}
        <div className="chatbot-message-content leading-normal font-medium text-sm sm:text-base">
          {isStatusMessage ? (
            <div className="flex items-center space-x-3">
              <TypingIndicator />
              <span className="opacity-80 font-medium">{messageText}</span>
            </div>
          ) : (
            <>
              {hasVisibleContent && (
                <span className="inline">
                  <MessageContent
                    content={messageText}
                    isCustomerMessage={isCustomer}
                    isTyping={useLightweightContentRendering}
                    onButtonClick={onButtonClick}
                  />
                  {showCursor && (
                    <span
                      className="inline-block w-[2px] h-4 align-text-bottom bg-current opacity-70 ml-0.5 animate-pulse"
                      aria-hidden="true"
                    />
                  )}
                </span>
              )}
              {hasInlineStatus && (
                <div
                  className={`flex items-center space-x-3 min-h-6 ${
                    hasVisibleContent ? 'mt-2 opacity-80' : ''
                  }`}
                  aria-live="polite"
                >
                  <TypingIndicator />
                  <span className="font-medium">{displayStatusText}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Copy buttons - positioned on right edge, stacked vertically, shown on hover */}
        {messageText && !isStatusMessage && (
          <div 
            className={`chatbot-copy-container absolute top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-md bg-card/90 backdrop-blur-sm border border-border/50 shadow-sm ${
              isCustomer ? 'left-0 -translate-x-[calc(100%-10px)]' : 'right-0 translate-x-[calc(100%-10px)]'
            }`}
          >
            {/* Copy all messages button - only on last agent message (shown above single copy) */}
            {isLastAgentMessage && !isCustomer && copyableText && (
              <button
                type="button"
                onClick={handleCopyAll}
                className={`chatbot-copy-all-button p-1.5 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 flex items-center justify-center
                  ${copiedAll 
                    ? 'text-green-500' 
                    : 'text-muted-foreground/80 hover:text-muted-foreground'
                  }`}
                title={copiedAll ? t('common.copied') : copyTooltip}
                aria-label={copyTooltip}
              >
                {copiedAll ? <Check size={14} /> : <Files size={14} />}
              </button>
            )}
            
            {/* Copy single message button */}
            <button
              type="button"
              onClick={handleCopySingle}
              className={`chatbot-copy-button p-1.5 rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 flex items-center justify-center
                ${copiedSingle 
                  ? 'text-green-500' 
                  : 'text-muted-foreground/80 hover:text-muted-foreground'
                }`}
              title={copiedSingle ? t('common.copied') : t('message.copyMessage')}
              aria-label={t('message.copyMessage')}
            >
              {copiedSingle ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        )}

        {/* Draft toggle for agent messages - only shown in debug mode */}
        {debugMode && hasDraft && (
          <div className="mt-2 pt-2 border-t border-border/30">
            <button
              onClick={() => setShowDraft(!showDraft)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <FileText size={12} />
              <span>{showDraft ? t('message.hideDraft') : t('message.showDraft')}</span>
              {showDraft ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {showDraft && (
              <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground italic">
                <span className="font-medium not-italic">{t('message.draft')}:</span>
                <p className="mt-1">{message.draft}</p>
              </div>
            )}
          </div>
        )}

        {/* Show logs button - only shown in debug mode */}
        {onShowLogs && !isCustomer && (
          <div className="mt-2 -mb-1">
            <button
              onClick={onShowLogs}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
              title={t('message.showLogs')}
            >
              <FileText size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const MessageBubble = memo(MessageBubbleComponent, messageBubblePropsEqual);
MessageBubble.displayName = 'MessageBubble';
